import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';

const VehicleRegistrationServicePage: React.FC = () => {
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Nevada Vehicle Registration Service Las Vegas - Professional DMV Help | Speedy DMV',
    description: 'Professional vehicle registration service in Las Vegas Nevada. New registrations, renewals, out-of-state transfers. Expert same-day processing. Licensed DMV provider at 4525 S Sandhill Rd. Call 702.979.7202.',
    keywords: 'vehicle registration service Las Vegas, Nevada registration help, DMV registration service Las Vegas, out-of-state vehicle registration Nevada, car registration Las Vegas NV',
    canonical: `${siteUrl}/vehicle-registration-service`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateServiceSchema(
          'Nevada Vehicle Registration Service',
          'Complete vehicle registration services in Las Vegas Nevada including new registrations, renewals, and out-of-state transfers with expert guidance and same-day processing.',
          '120'
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
          { name: 'Vehicle Registration Service', url: '/vehicle-registration-service' }
        ])
      ]
    }
  });

  const requiredDocuments = [
    'Nevada Evidence of Insurance',
    'Vehicle Title (properly signed by seller)',
    'Odometer Disclosure (for 2011 or newer vehicles)',
    'Emission Inspection Report (if required)',
    'VIN Inspection (for out-of-state vehicles)',
    'Dealer\'s Report of Sale (if purchased from dealer)',
    'Proof of Identity and Residency'
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Gather Required Documents',
      description: 'Collect all necessary paperwork including title, insurance, and identification.'
    },
    {
      step: 2,
      title: 'Vehicle Inspection',
      description: 'Complete emissions testing if required in Clark or Washoe County. VIN inspection needed for out-of-state vehicles.'
    },
    {
      step: 3,
      title: 'Schedule Appointment',
      description: 'Call Speedy DMV Services to schedule your appointment and avoid long wait times.'
    },
    {
      step: 4,
      title: 'Complete Registration',
      description: 'Submit documents, pay fees, and receive your new Nevada registration and license plates.'
    }
  ];

  const toggleExpanded = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Nevada Vehicle Registration Services
              </h1>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                Professional vehicle registration services in Las Vegas. We handle all Nevada DMV registration requirements 
                with same-day processing and expert guidance through every step.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:702-979-7202" 
                   className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Call Now: (702) 979-7202
                </a>
                <a href="/appointment" 
                   className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Schedule Appointment
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Service Overview */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Complete Vehicle Registration Solutions
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                From new residents to vehicle purchases, we provide comprehensive registration services 
                that save you time and ensure compliance with Nevada DMV requirements.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-car-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">New Vehicle Registration</h3>
                <p className="text-gray-600">Register your newly purchased vehicle with all required documentation and inspections.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-refresh-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">Registration Renewal</h3>
                <p className="text-gray-600">Renew your existing Nevada vehicle registration with updated documentation.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-map-pin-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">Out-of-State Transfers</h3>
                <p className="text-gray-600">Transfer your out-of-state vehicle registration to Nevada with proper inspections.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Registration Process
              </h2>
              <p className="text-lg text-gray-600">
                Our streamlined process ensures your vehicle registration is completed efficiently and correctly.
              </p>
            </div>

            <div className="space-y-8">
              {processSteps.map((item, index) => (
                <div key={index} className="flex items-start space-x-6">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                    {item.step}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Required Documents */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Required Documents Checklist
              </h2>
              <p className="text-lg text-gray-600">
                Ensure you have all necessary documents before your appointment to avoid delays.
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-md">
                {requiredDocuments.map((doc, index) => (
                  <label key={index} className="flex items-center space-x-3 py-3 border-b border-gray-200 last:border-b-0 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                      checked={selectedDocuments.includes(doc)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDocuments([...selectedDocuments, doc]);
                        } else {
                          setSelectedDocuments(selectedDocuments.filter(item => item !== doc));
                        }
                      }}
                    />
                    <span className="text-gray-800 flex-1">{doc}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  question: "How long does vehicle registration take?",
                  answer: "With Speedy DMV Services, most vehicle registrations are completed the same day with an appointment. Processing time varies based on document completeness and any required inspections."
                },
                {
                  question: "Do I need an emissions test for my vehicle?",
                  answer: "Emissions testing is required in Clark County (Las Vegas area) and Washoe County (Reno area) for gasoline-powered vehicles. We can guide you to approved testing stations."
                },
                {
                  question: "What if I'm registering an out-of-state vehicle?",
                  answer: "Out-of-state vehicles require a VIN inspection before registration. We coordinate this inspection as part of our comprehensive service."
                },
                {
                  question: "Can I register a vehicle without the title?",
                  answer: "A properly signed title is required for vehicle registration. If you don't have the title, we can help you obtain a duplicate from the appropriate state DMV."
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md">
                  <button
                    className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none"
                    onClick={() => toggleExpanded(`faq-${index}`)}
                  >
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                    <i className={`ri-arrow-${expandedSection === `faq-${index}` ? 'up' : 'down'}-s-line text-xl text-gray-500`}></i>
                  </button>
                  {expandedSection === `faq-${index}` && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-blue-900 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Register Your Vehicle?
            </h2>
            <p className="text-xl mb-8">
              📞 Appointments Required – Please Call Ahead
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:702-979-7202" 
                 className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Call (702) 979-7202
              </a>
              <a href="/location" 
                 className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Visit Our Location
              </a>
            </div>
            <div className="mt-6 text-gray-300">
              <p>4525 S Sandhill Rd, Las Vegas, NV</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default VehicleRegistrationServicePage;
